import { Popup } from '../../../../ui/popup/popup';
import {
    ApplePayReferrer,
    FREE_TRIAL_ABUSE_ERROR_CODE,
    getCompleteApplePayUrlByReferrer,
    IApplePayData, sendApplePayEventToGA, SUBSCRIPTION_ADD_PAYMENT_TOKEN_WARNING
} from '../domain/apple-pay-provider';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { DEFAULT_ERROR, post } from '../../../../DAL/data-provider';
import Preloader from '../../../../ui/svg/common/preloader';
import { getHiddenDataString } from '../../../../domain/hidden-data-provider';
import { formatBridgerPayError } from '../domain/bridger-pay-provider';

interface IApplePayPopup {
    applePayData: IApplePayData;
    referrer: ApplePayReferrer;
    onClose: (error?: string) => void;
    onComplete: (orderGUID: string, isInAppPurchase: boolean) => void;
}

/**
 * https://developers.bridgerpay.com/docs/apple-pay#use-front-end-events-to-ensure-a-seamless-apple-pay-experience
 * Events: https://developers.bridgerpay.com/docs/event-capture#events-descriptions
 */
const ApplePayPopup = ({ applePayData, referrer, onClose, onComplete } : IApplePayPopup) => {

    const [loading, setLoading] = useState(false);
    const [isAuthorized, setIsAuthorized] = useState(false);

    const scriptContainerRef = useRef<HTMLDivElement>(null);

    const closePopup = useCallback((error?: string) => {
        onClose(error);
    }, [
        onClose,
    ]);

    useEffect(() => {
        if (!scriptContainerRef.current) return;

        const {
            cashierKey,
            cashierToken,
            orderGUID,
            orderRnd,
            isInAppPurchase,
        } = applePayData;

        const walletButtonIsReady = (evt: Event) => {
            const result = (evt as CustomEvent).detail;
            console.log('wallet-button-is-ready', result);
            sendApplePayEventToGA('ApplePay Wallet Ready', orderGUID);
        };

        /**
         * PAYMENT SUCCESS.
         * ----------------
         * The result of the transaction located in the detail property.
         */
        const walletResult = (evt: Event) => {

            const result = (evt as CustomEvent).detail;
            console.log('wallet-result', result)
            sendApplePayEventToGA('ApplePay Wallet Result', `${ orderGUID }-${ result.status }${ result.reason ? `-${ result.reason }` : '' }`);

            const completeOrder = async () => {

                const completeOrderResult = await post(getCompleteApplePayUrlByReferrer(referrer), {
                    orderGUID,
                    orderRnd,
                });

                const statusCode = completeOrderResult?.data?.relatedData?.statusCode ?? -1;

                if(statusCode === FREE_TRIAL_ABUSE_ERROR_CODE) {
                    window.location.href = '/cart/trial-subscription-error';
                    return;
                }

                if(statusCode === SUBSCRIPTION_ADD_PAYMENT_TOKEN_WARNING) {
                    window.location.href = `/account/change-payment-method/completed?msg=1`;
                    return;
                }

                if(completeOrderResult &&
                    completeOrderResult.data &&
                    completeOrderResult.data.relatedData &&
                    completeOrderResult.data.relatedData.isValid &&
                    completeOrderResult.data.relatedData.statusCode === 0) {
                    onComplete(orderGUID, isInAppPurchase);
                    return;
                }

                // Handle error ----------------------
                closePopup(DEFAULT_ERROR);
            };

            /*
            OK result example:
            ------------------
            amount: 10
            currency: "USD"
            id: "acc5b4ce-d682-452b-b3b5-14d4cf76a6e8"
            payment_method: "wallet_apple_pay_direct"
            processing_date: "2025-08-05 11:36:40"
            psp_response: undefined
            status: "authorized"
            transaction_id: "pi_3RsjAaAHdMIPgqR72mqeNJlq"
             */

            /*
            Error result example ?
            ------------------
            amount: "49.99"
            currency: "USD"
            payment_method: "wallet_apple_pay"
            reason: "Failed to create BridgerPay transaction"
            status: "declined"
             */
            if(result?.status === 'authorized') {
                setIsAuthorized(true);
                completeOrder();
            }
            else{
                const bpError = formatBridgerPayError(result?.reason || DEFAULT_ERROR);
                closePopup(bpError);
            }
        };

        const walletCanceled = (evt: Event) => {
            const result = (evt as CustomEvent).detail;
            console.log('wallet-cancelled', result);
            sendApplePayEventToGA('ApplePay Wallet Canceled', orderGUID);
            closePopup();
        };

        /**
         * Wallet is processing payment.
         * Triggered when a transaction is successfully processed.
         */
        const walletProcessingPayment = (evt: Event) => {
            const result = (evt as CustomEvent).detail;
            console.log('wallet-processing-payment', result);
            sendApplePayEventToGA('ApplePay Wallet Processing', orderGUID);
            setLoading(true);
        };

        /**
         * PSP not found in the route.
         * Should not occur on production.
         */
        const noWalletProviderFound = (evt: Event) => {
            const result = (evt as CustomEvent).detail;
            console.log('no-wallet-provider-found', result);
            sendApplePayEventToGA('ApplePay Wallet No Provider', orderGUID);
            closePopup(DEFAULT_ERROR);
        };

        /**
         * User device is not supported.
         * Unsupported device or a browser.
         */
        const walletProviderCannotBeUsed = (evt: Event) => {
            const result = (evt as CustomEvent).detail;
            console.log('wallet-provider-cannot-be-used', result);
            sendApplePayEventToGA('ApplePay Wallet Unsupported', orderGUID);
            closePopup(DEFAULT_ERROR);
        };

        window.addEventListener(`[bp][checkout:${ cashierKey }]:wallet-button-is-ready`, walletButtonIsReady, false);
        window.addEventListener(`[bp][checkout:${ cashierKey }]:wallet-cancelled`, walletCanceled, false);
        window.addEventListener(`[bp][checkout:${ cashierKey }]:wallet-result`, walletResult, false);
        window.addEventListener(`[bp][checkout:${ cashierKey }]:wallet-processing-payment`, walletProcessingPayment, false);
        window.addEventListener(`[bp][checkout:${ cashierKey }]:no-wallet-provider-found`, noWalletProviderFound, false);
        window.addEventListener(`[bp][checkout:${ cashierKey }]:wallet-provider-cannot-be-used`, walletProviderCannotBeUsed, false);

        const script = document.createElement('script');
        const bpDomain = getHiddenDataString('bridger-pay-js-api-domain');
        script.src = `https://${ bpDomain }/v2/launcher`;
        script.id = 'bridgerpay-script';
        script.setAttribute('data-cashier-key', cashierKey);
        script.setAttribute('data-cashier-token', cashierToken);
        script.setAttribute('data-button-mode', 'wallet');
        scriptContainerRef.current.innerHTML = '';
        scriptContainerRef.current.appendChild(script);
        console.log('ApplePay BP script is added.');

        return () => {
            window.removeEventListener(`[bp][checkout:${ cashierKey }]:wallet-button-is-ready`, walletButtonIsReady, false);
            window.removeEventListener(`[bp][checkout:${ cashierKey }]:wallet-cancelled`, walletCanceled, false);
            window.removeEventListener(`[bp][checkout:${ cashierKey }]:wallet-result`, walletResult, false);
            window.removeEventListener(`[bp][checkout:${ cashierKey }]:wallet-processing-payment`, walletProcessingPayment, false);
            window.removeEventListener(`[bp][checkout:${ cashierKey }]:no-wallet-provider-found`, noWalletProviderFound, false);
            window.removeEventListener(`[bp][checkout:${ cashierKey }]:wallet-provider-cannot-be-used`, walletProviderCannotBeUsed, false);
        };
    }, [
        applePayData,
        referrer,
        closePopup,
        onComplete,
    ]);

    return (
        <Popup
            width="420px"
            height="270px"
            zIndex={ 10000 }
            close={ closePopup }>
            <div className="flex flex-col justify-center items-center w-full h-full my-4 text-center px-4">

                <p className="mb-2 text-xl font-bold">Apple Pay</p>

                {
                    !isAuthorized &&
                    <p className="mb-4">
                        To complete your order with Apple Pay<br/>
                        please click the button below
                    </p>
                }

                {
                    isAuthorized &&
                    <p className="mb-4">
                        Processing your payment, please stay on this page.
                    </p>
                }

                <div
                    className={ loading ? 'hidden' : '' }
                    data-id="apply-pay-btn"
                    ref={ scriptContainerRef }>
                    <Preloader width="35px" height="35px" color="#4f4f4f" />
                </div>

                {
                    loading &&
                    <div className="flex justify-center mt-4">
                        <Preloader width="35px" height="35px" color="#4f4f4f" />
                    </div>
                }
            </div>
        </Popup>
    )
};

export default ApplePayPopup;